'use client'

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Ellipsis, Pencil, Send, Trash } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import type { RenderLink } from '@ui/web/utils'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  toast,
} from '@ui/web/components'
import { ReadBookIcon } from '@ui/web/icon'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'
import { MyBookCollectionForm, SentCollectionToChildForm } from '../../../forms'
import { BookModal } from '../../../lib'
import { DeleteBookCollectionModal } from './delete-modal'
import { Empty } from './empty'

interface MyBookCollectionPageProps {
  type: 'web' | 'school'
  myBookCollectionId?: string
  withShareCta?: boolean
  className?: string
  withDropdownMenu?: boolean
  /** When viewing a sent collection as the sender (e.g. terkirim detail). */
  sentCollectionId?: string
  /** When viewing a sent collection as the recipient (e.g. kiriman/inbox detail). */
  sentCollectionIdAsRecipient?: string
  /**
   * When viewing a collection shared through the generic grant tables, keyed by the
   * collection's own id. Unlike `sentCollectionIdAsRecipient` this path does not mark
   * anything read — the notification feed owns read state.
   */
  sharedCollectionId?: string
  backButton: React.ReactNode
  onSuccessDeleteBookCollection?: () => void
  renderReadBookLink: RenderLink
  renderMakeNewChildLink: RenderLink
}

export function MyBookCollectionPage({
  type: _type,
  myBookCollectionId,
  withShareCta = false,
  className,
  withDropdownMenu = false,
  sentCollectionId,
  sentCollectionIdAsRecipient,
  sharedCollectionId,
  backButton,
  onSuccessDeleteBookCollection,
  renderReadBookLink,
  renderMakeNewChildLink,
}: MyBookCollectionPageProps) {
  if (!myBookCollectionId && !sentCollectionId && !sentCollectionIdAsRecipient && !sharedCollectionId) {
    throw new Error(
      'Either myBookCollectionId, sentCollectionId, sentCollectionIdAsRecipient, or sharedCollectionId must be provided',
    )
  }

  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const [showMore, setShowMore] = useState(false)
  const [openDeleteBookCollectionDialog, setOpenDeleteBookCollectionDialog] = useState(false)
  const [openMakeNewBookCollectionDialog, setOpenMakeNewBookCollectionDialog] = useState(false)
  const [openShareBookCollectionDialog, setOpenShareBookCollectionDialog] = useState(false)

  const isViewingAsRecipient = Boolean(sentCollectionIdAsRecipient)

  const legacyQueryOptions = isViewingAsRecipient
    ? trpc.myBookCollection.getOneBookCollectionBySentIdAsRecipient.queryOptions({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sentCollectionId: sentCollectionIdAsRecipient!,
      })
    : trpc.myBookCollection.getOneBookCollection.queryOptions({
        id: myBookCollectionId,
        sentCollectionId,
      })

  // Two queries rather than one options ternary: `getSharedCollection` returns a
  // narrower row (no `sentBookCollections` — a recipient has no business seeing who
  // else the sender picked), and mixing the two option objects in a ternary makes
  // TanStack Query's generics unresolvable.
  const legacyQuery = useQuery({ ...legacyQueryOptions, enabled: !sharedCollectionId })

  const sharedQuery = useQuery({
    ...trpc.myBookCollection.getSharedCollection.queryOptions({ collectionId: sharedCollectionId ?? '' }),
    enabled: Boolean(sharedCollectionId),
  })

  const { isPending, isError, refetch } = sharedCollectionId ? sharedQuery : legacyQuery

  // `sentBookCollections: []` is the honest projection of the recipient view, not a
  // placeholder: the send list is sender-only data. Delete and share CTAs are gated
  // behind props the shared-collection route does not pass.
  const data = sharedCollectionId
    ? sharedQuery.data
      ? { ...sharedQuery.data, sentBookCollections: [] }
      : undefined
    : legacyQuery.data

  const { mutate: readSentCollectionAsRecipient } = useMutation(
    trpc.myBookCollection.updateSentBookCollectionToUserBySentCollectionId.mutationOptions({
      onSuccess: async () => {
        if (sentCollectionIdAsRecipient) {
          await queryClient.invalidateQueries(
            trpc.myBookCollection.getOneBookCollectionBySentIdAsRecipient.queryOptions({
              sentCollectionId: sentCollectionIdAsRecipient,
            }),
          )
          await queryClient.invalidateQueries(trpc.myBookCollection.getUnreadSentBookCollections.queryOptions())
          await queryClient.invalidateQueries(trpc.myBookCollection.getMySentBookCollections.queryOptions())
        }
      },
    }),
  )

  useEffect(() => {
    if (sentCollectionIdAsRecipient) {
      readSentCollectionAsRecipient({
        sentBookCollectionId: sentCollectionIdAsRecipient,
        data: { readAt: new Date() },
      })
    }
  }, [sentCollectionIdAsRecipient])

  if (isError) {
    return (
      <section className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-10', className)}>
        <div className='srd:flex srd:flex-col srd:gap-y-8'>
          <div className='srd:flex srd:justify-start'>{backButton}</div>
        </div>

        <div className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:h-fit srd:min-h-[40dvh] srd:text-center'>
          <p className={cn('srd:text-xl srd:font-semibold srd:text-neutrals-white')}>Oops, Terjadi Kesalahan</p>
          <p className={cn('srd:text-sm srd:text-neutrals-white')}>Gagal memuat koleksi. Silakan coba lagi.</p>
          <Button
            onClick={() => refetch()}
            className={cn('srd:px-6 srd:py-2 srd:rounded-full', 'srd:bg-main-blue-20 srd:text-main-blue-100')}
          >
            Coba Lagi
          </Button>
        </div>
      </section>
    )
  }

  if (isPending || !data) {
    return (
      <section className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-10', className)}>
        <div className='srd:flex srd:flex-col srd:gap-y-8'>
          <div className='srd:flex srd:justify-start'>{backButton}</div>
          <header className='srd:flex srd:justify-between srd:items-center srd:gap-x-4 srd:gap-y-6'>
            <div className='srd:flex srd:flex-col srd:gap-2 srd:w-full'>
              <Skeleton className='srd:w-full srd:max-w-[200px] srd:h-10 srd:rounded-full' />
              <Skeleton className='srd:w-full srd:max-w-[150px] srd:h-8 srd:rounded-full' />
            </div>

            <Skeleton className='srd:w-full srd:max-w-[100px] srd:h-8 srd:rounded-full' />
          </header>
        </div>

        <div className='srd:w-full srd:grid srd:2xl:grid-cols-6 srd:xl:grid-cols-5 srd:lg:grid-cols-4 srd:md:grid-cols-3 srd:grid-cols-2 srd:gap-4'>
          {new Array(8).fill(0).map((_, index) => (
            <Skeleton
              key={`my-collection-${index}`}
              className='srd:w-full srd:h-[225px] srd:aspect-3/4 srd:rounded-md'
            />
          ))}
        </div>
      </section>
    )
  }

  return (
    <>
      <section className={cn('srd:w-full srd:flex srd:flex-col srd:gap-y-10', className)}>
        <div className='srd:flex srd:flex-col srd:gap-y-8'>
          <div className='srd:flex srd:justify-start'>{backButton}</div>
          <header className='srd:flex srd:justify-between srd:items-center srd:gap-x-4 srd:gap-y-6 srd:flex-wrap srd:w-full'>
            <div
              className={cn(
                'srd:flex srd:flex-col srd:gap-2 srd:w-fit srd:max-w-[500px] srd:break-all srd:text-neutrals-white',
              )}
            >
              <h1 className='srd:text-2xl srd:font-bold'>{data.name}</h1>
              <p className='srd:text-sm'>{data.description}</p>
            </div>

            <div className='srd:flex srd:items-center srd:gap-x-4'>
              <p className={cn('srd:text-sm srd:font-semibold srd:text-complementary-yellow-100')}>
                {data.myBookCollectionToBooks.length} buku
              </p>

              {withShareCta && (
                <Button onClick={() => setOpenShareBookCollectionDialog(true)} className='srd:gap-x-2'>
                  <Send className='srd:size-4' />
                  Bagikan ke anak
                </Button>
              )}

              {withDropdownMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant='ghost'
                        className={cn(
                          'srd:w-fit srd:h-fit srd:p-0 srd:rounded-full srd:hover:bg-none srd:hover:bg-transparent srd:hover:opacity-70 srd:text-neutrals-white',
                        )}
                      >
                        <Ellipsis className='srd:w-4 srd:h-4' />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align='end' className='srd:min-w-[200px]'>
                    <DropdownMenuItem onClick={() => setOpenMakeNewBookCollectionDialog(true)}>
                      <Pencil className='srd:w-4 srd:h-4 srd:mr-2' />
                      Edit koleksi
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      className='srd:text-main-red-100'
                      onClick={() => setOpenDeleteBookCollectionDialog(true)}
                    >
                      <Trash className='srd:w-4 srd:h-4 srd:mr-2' />
                      Hapus koleksi
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </header>
        </div>

        <div className='srd:w-full srd:grid srd:2xl:grid-cols-6 srd:xl:grid-cols-5 srd:lg:grid-cols-4 srd:md:grid-cols-3 srd:grid-cols-2 srd:gap-4'>
          {(() => {
            if (data.myBookCollectionToBooks.length === 0)
              return <Empty sentCollectionId={sentCollectionId ?? sentCollectionIdAsRecipient} />

            const booksToShow = showMore ? data.myBookCollectionToBooks : data.myBookCollectionToBooks.slice(0, 10)

            return (
              <>
                <AnimatePresence>
                  {booksToShow.map((book, index) => (
                    <motion.div
                      key={`book-result-${book.bookId}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className='srd:w-full srd:h-fit srd:flex srd:items-center srd:justify-center'
                    >
                      <BookModal data={book.book} withAnimation={false}>
                        <Button className='srd:w-full' asChild>
                          {renderReadBookLink({
                            children: (
                              <>
                                <ReadBookIcon className='srd:size-6 srd:mr-2 srd:shrink-0' />
                                Baca buku ini
                              </>
                            ),
                            meta: { bookId: book.book.id, bookSlug: book.book.slug },
                          })}
                        </Button>
                      </BookModal>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {data.myBookCollectionToBooks.length > 10 && (
                  <motion.div
                    className='srd:col-span-full srd:flex srd:justify-center srd:mt-4'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Button
                      onClick={() => setShowMore(!showMore)}
                      className={cn('srd:px-6 srd:py-2 srd:rounded-full srd:bg-main-blue-20 srd:text-main-blue-100')}
                    >
                      {showMore ? (
                        <>
                          Lihat lebih sedikit <ChevronUp className='srd:w-4 srd:h-4 srd:ml-2' />
                        </>
                      ) : (
                        <>
                          Lihat semua <ChevronDown className='srd:w-4 srd:h-4 srd:ml-2' />
                        </>
                      )}
                    </Button>
                  </motion.div>
                )}
              </>
            )
          })()}
        </div>
      </section>

      <Dialog open={openMakeNewBookCollectionDialog} onOpenChange={setOpenMakeNewBookCollectionDialog} modal>
        <DialogContent className='srd:w-full srd:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle>Buat Koleksi</DialogTitle>

            <DialogDescription className='srd:sr-only'>
              Buat koleksi baru untuk memudahkan pencarian buku.
            </DialogDescription>
          </DialogHeader>

          <MyBookCollectionForm
            withAreYouSureDialog
            initialData={{ myBookCollectionId: data.id, name: data.name, description: data.description ?? undefined }}
            onFinishSubmitting={async (data) => {
              toast.success('Koleksi berhasil diperbarui!')

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getOneBookCollection.queryKey({ id: data.id }),
              })

              setOpenMakeNewBookCollectionDialog(false)
            }}
            onCancel={() => setOpenMakeNewBookCollectionDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {onSuccessDeleteBookCollection && (
        <DeleteBookCollectionModal
          data={data}
          open={openDeleteBookCollectionDialog}
          onOpenChange={setOpenDeleteBookCollectionDialog}
          onSuccess={onSuccessDeleteBookCollection}
        />
      )}

      {withShareCta && (
        <AlertDialog open={openShareBookCollectionDialog} onOpenChange={setOpenShareBookCollectionDialog}>
          <AlertDialogContent className='srd:min-w-0 srd:max-h-[90dvh] srd:w-full srd:max-w-[800px] srd:overflow-x-hidden srd:overflow-y-auto'>
            <AlertDialogHeader>
              <AlertDialogTitle>Kirim Koleksi ke Anak</AlertDialogTitle>
              <AlertDialogDescription className='sr-only'>Kirim koleksi ke anak yang dipilih.</AlertDialogDescription>
            </AlertDialogHeader>

            <div className='srd:min-w-0 srd:w-full'>
              <SentCollectionToChildForm
                renderMakeNewChildLink={renderMakeNewChildLink}
                withAreYouSureDialog
                myBookCollectionId={data.id}
                onSuccess={async () => {
                  toast.success('Koleksi berhasil dikirim!')
                  await queryClient.invalidateQueries({
                    queryKey: trpc.myBookCollection.getOneBookCollection.queryKey({ id: data.id }),
                  })
                  await queryClient.invalidateQueries({
                    queryKey: trpc.myBookCollection.getAllSentBookCollections.queryKey(),
                  })
                  await queryClient.invalidateQueries({
                    queryKey: trpc.myBookCollection.getAllBookCollections.queryKey(),
                  })
                  setOpenShareBookCollectionDialog(false)
                }}
                onCancel={() => setOpenShareBookCollectionDialog(false)}
              />
            </div>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}
